// Copyright (c) 2015-present Mattermost, Inc. All Rights Reserved.
// See LICENSE.txt for license information.

import {type ReactNode, type ComponentProps, useState, type CSSProperties, useMemo, forwardRef} from 'react';

import OverlayTrigger from 'components/overlay_trigger';
import Tooltip from 'components/tooltip';
import Constants from 'utils/constants';

type Props = {
    id: string;
    content?: ReactNode;
    children: ReactNode;
    onEntered?: () => void;
    animation?: boolean;
    delayShow?: number;
    className?: string;
    arrowOffsetTop?: number | string;
    style?: CSSProperties;
}

const SimpleTooltip = forwardRef<HTMLDivElement, Props>(({
    id,
    content,
    children,
    placement = 'top',
    className = 'hidden-xs',
    delayShow = Constants.OVERLAY_TIME_DELAY,
    style,
    ...props
}: Props & Omit<ComponentProps<typeof OverlayTrigger>, 'overlay'>) => {
    return (
        <OverlayTrigger
            {...props}
            delayShow={delayShow}
            placement={placement}
            overlay={
                <Tooltip
                    id={id}
                    style={style}
                    className={className}
                    placement={placement}
                >
                    {content}
                </Tooltip>
            }
        >
            {children}
        </OverlayTrigger>
    );
});

export default SimpleTooltip;

export function useSynchronizedImmediate(): [Partial<ComponentProps<typeof SimpleTooltip>>, (isImmediate: boolean) => void] {
    const [isImmediate, setImmediate] = useState(false);

    return [
        useMemo((): Partial<ComponentProps<typeof SimpleTooltip>> => ({
            onEntered: () => setImmediate(true),
            animation: !isImmediate,
            delayShow: isImmediate ? 0 : undefined,
        }), [isImmediate, setImmediate]),
        setImmediate,
    ];
}
